import type { SendMailInHtmlFormatRequest } from 'apiConnector/summary';
import { ProductSummaryApiConnector } from 'apiConnector/summary';
import { useCostAndChargesDescription } from 'components/quote/costsAndCharges/CostsAndCharges.helper';
import { FormattedMessage } from 'components/shared/Intl';
import { useUserNotification } from 'hooks/useUserNotification';
import { FC, useContext } from 'react';
import { lastValueFrom } from 'rxjs';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';
import { httpClient } from 'utils/http/httpClient';

import type { MailSenderProps } from './MailSender.connect';
import { connectSummaryMailSender } from './MailSender.connect';

export const Component: FC<MailSenderProps> = ({
  availableCustodians,
  externalQuoteId,
  formEnrichedData,
  isDarkMode,
  isSolved,
  solveResult,
  kidDocumentation,
  productGovernanceDocumentation,
  optimizedBasketResults,
  costsAndChargesResults,
  costsAndChargesCurrency,
  defaultUnderlyingCodeType,
  languageCode,
  quoteWorkspaceVersion,
  mustForceAdvancedMode,
  greeks,
  equityGreeks,
}) => {
  const notification = useUserNotification();
  const costsAndChargesDescription = useCostAndChargesDescription(costsAndChargesResults);

  const sendHtmlEmail = (): void => {
    const htmlRequest: SendMailInHtmlFormatRequest = {
      availableCustodians,
      externalQuoteId,
      formData: formEnrichedData,
      isDarkMode,
      isSolved,
      solveResult,
      kidDocumentation,
      productGovernanceDocumentation,
      optimizedBasketResults,
      costsAndChargesResults: [...costsAndChargesResults].sort(
        (a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name),
      ),
      costsAndChargesCurrency,
      costsAndChargesDescription,
      defaultUnderlyingCodeType,
      languageCode,
      quoteWorkspaceVersion,
      mustForceAdvancedMode,
      greeks,
      equityGreeks,
    };

    lastValueFrom(ProductSummaryApiConnector(httpClient).sendMaiInHtmlFormat(htmlRequest)).then(
      onMailSentWithSuccess,
      onMailSentWithError,
    );
  };
  const logAnalyticsCategory = useContext(WorkspaceAnalyticCategoryContext);
  const onMailSentWithError = (_: Error): void => {
    notification.error({
      title: { id: 'toast.mail' },
      content: [{ id: 'mailSentWithError' }],
      closeAutomatically: false,
    });
  };

  const onMailSentWithSuccess = (): void => {
    notification.success({ title: { id: 'toast.mail' }, content: [{ id: 'mailSentWithSuccess' }] });
  };

  const onSendToMe = (): void => {
    logAnalytics.trackEvent(logAnalyticsCategory, 'Display summary');
    sendHtmlEmail();
  };

  return (
    <button className="btn btn-xl btn-primary" onClick={onSendToMe} data-e2e="sendToMe-product-summary">
      <FormattedMessage id="sendToMe" />
    </button>
  );
};

export const SummaryMailSender = connectSummaryMailSender(Component);
